import { Link, useRouterState } from '@tanstack/react-router';
import { Icon } from './Icon.tsx';
import { useAuthStore } from '../store/auth.ts';
import { dv } from '../i18n/dv.ts';
import { hasCensus, hasZone } from '../features/zone/access.ts';

type NavItem =
  | { kind: 'link'; to: string; label: string; icon: string; matchPrefix?: string }
  | { kind: 'action'; action: 'logout'; label: string; icon: string };

export function BottomNav(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  if (!user) return null;

  const census = hasCensus(user);
  const zone = hasZone(user);
  const logoutItem: NavItem = { kind: 'action', action: 'logout', label: dv.auth.logout, icon: 'logout' };
  const profileItem: NavItem = { kind: 'link', to: '/profile', label: dv.nav.profile, icon: 'person' };
  const dashboardItem: NavItem = { kind: 'link', to: '/', label: dv.nav.dashboard, icon: 'dashboard' };
  const censusItem: NavItem = { kind: 'link', to: '/houses', label: 'Census', icon: 'home', matchPrefix: '/houses' };
  const surveysItem: NavItem = { kind: 'link', to: '/surveys', label: 'Surveys', icon: 'assignment', matchPrefix: '/surveys' };
  const zoneItem: NavItem = { kind: 'link', to: '/zone', label: 'Zone', icon: 'report', matchPrefix: '/zone' };

  let items: NavItem[];
  if (user.role === 'SYS_ADMIN') {
    items = [
      { kind: 'link', to: '/admin/orgs', label: dv.nav.orgs, icon: 'apartment', matchPrefix: '/admin' },
      profileItem,
      logoutItem,
    ];
  } else if (census && zone) {
    items = [dashboardItem, censusItem, surveysItem, zoneItem, profileItem, logoutItem];
  } else if (zone) {
    // Zone-only users have no census dashboard; their Dashboard is the zone one.
    items = [
      { kind: 'link', to: '/zone/dashboard', label: 'Dashboard', icon: 'insights' },
      zoneItem,
      profileItem,
      logoutItem,
    ];
  } else {
    items = [
      dashboardItem,
      censusItem,
      surveysItem,
      ...((user.isOrgAdmin || user.role === 'ADMIN')
        ? [{ kind: 'link' as const, to: '/dhaftharu', label: dv.nav.dhaftharu, icon: 'menu_book', matchPrefix: '/dhaftharu' }]
        : []),
      // Settings lives in the top header (next to notifications) on mobile.
      profileItem,
      logoutItem,
    ];
  }

  // The zone dashboard has its own tab only for zone-only users; elsewhere it
  // counts as part of "Zone".
  const zoneDashboardTab = items.some((i) => i.kind === 'link' && i.to === '/zone/dashboard');

  return (
    <nav
      aria-label={dv.nav.dashboard}
      className="dock md:hidden fixed inset-x-3 bottom-3 z-40"
      style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
    >
      <ul className="flex items-stretch">
        {items.map((item) => {
          const active =
            item.kind === 'link'
              ? item.to === '/zone'
                ? pathname.startsWith('/zone') && !(zoneDashboardTab && pathname === '/zone/dashboard')
                : item.matchPrefix
                ? pathname.startsWith(item.matchPrefix)
                : pathname === item.to
              : false;

          const isDestructive = item.kind === 'action' && item.action === 'logout';

          // No `tracking-tight`: negative letter-spacing distorts Thaana nav
          // labels on Windows. (font-serif active label is Thaana-heading too.)
          const labelClasses = isDestructive
            ? 'whitespace-nowrap text-[10.5px] leading-none text-destructive'
            : active
              ? 'whitespace-nowrap text-[12px] leading-none font-serif text-ink'
              : 'whitespace-nowrap text-[10.5px] leading-none text-muted-foreground';

          // M3 navigation bar: the active destination is a tonal pill behind a
          // filled icon — that pill replaces the old paper-style dot indicator.
          const inner = (
            <>
              <span
                className={[
                  'grid h-8 w-12 place-items-center rounded-full transition-colors',
                  active
                    ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]'
                    : isDestructive
                      ? 'text-destructive'
                      : 'text-muted-foreground',
                ].join(' ')}
              >
                <Icon name={item.icon} size={22} fill={active} />
              </span>
              <span className={labelClasses}>{item.label}</span>
            </>
          );

          const itemClasses =
            'flex h-16 w-full flex-col items-center justify-center gap-1 active:scale-95 transition-transform';

          if (item.kind === 'action') {
            return (
              <li key="logout" className="min-w-0 flex-1">
                <button
                  type="button"
                  onClick={logout}
                  aria-label={item.label}
                  className={`${itemClasses} text-destructive`}
                >
                  {inner}
                </button>
              </li>
            );
          }

          return (
            <li key={item.to} className="min-w-0 flex-1">
              <Link
                to={item.to}
                aria-label={item.label}
                aria-current={active ? 'page' : undefined}
                className={itemClasses}
              >
                {inner}
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
